<!-- ModalInfo.vue -->
<script setup lang="ts">
  import { IModalData } from '@/types/index'
  import { IconButton, Typography, Shimmer } from '@components/index'
  import { fetchComplement } from '@server/index'

  // Props do componente ModalInfo.
  const props = defineProps<IModalData>()

  // Variáveis reativas
  const complementID = ref<string>(props.id)
  const isActive = ref<boolean>(false)
  const isScrollEnabled = ref<boolean>(false)
  const haveBack = ref<boolean>(false)
  const finishMount = ref<boolean>(false)
  const idHistory = ref<string[]>([])
  const { data: complementData, loading: isLoading } = fetchComplement(complementID.value)

  const emit = defineEmits(['onClose'])

  const openModal = () => {
    isScrollEnabled.value = true
    setTimeout(() => {
      isActive.value = true
    }, 100)
  }

  const closeModal = () => {
    isActive.value = false
    isScrollEnabled.value = false
    setTimeout(() => {
      emit('onClose', false)
    }, 300)
  }

  const scrollContentToTop = () => {
    const contentElement = document.querySelector('.modal-content')
    if (contentElement) {
      contentElement.scrollTop = 0
    }
    checkBackAndNext()
  }

  onMounted(() => {
    openModal()
    finishMount.value = true
  })

  // Verifica se é possível navegar para trás.
  const checkBackAndNext = () => {
    if (complementData.value && complementData.value.complement) {
      if (complementData.value.id !== props.id) {
        haveBack.value = true
      } else {
        haveBack.value = false
      }
    } else {
      haveBack.value = false
    }
  }

  const handleSpanClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const dataId = target.getAttribute('data-id')
    if (dataId) {
      idHistory.value.push(complementID.value)
      complementID.value = dataId
      loadComplementData()
    }
  }

  const handleClickBack = () => {
    if (idHistory.value.length > 0) {
      const idBack = idHistory.value.pop()
      if (idBack) {
        complementID.value = idBack
        loadComplementData()
      }
    }
  }

  // Função para carregar os dados de complementação.
  const loadComplementData = () => {
    const { data, loading } = fetchComplement(complementID.value)
    complementData.value = data.value
    isLoading.value = loading.value
    watchEffect(() => {
      if (complementData.value) {
        isLoading.value = false
        scrollContentToTop()
      }
    })
  }

  watchEffect(() => {
    if (finishMount.value) {
      loadComplementData()
    }
  })
</script>

<template>
  <div class="box-content d-flex justify-content-end">
    <div
      :class="['backdrop', { active: isActive }]"
      @click="closeModal"
    />
    <div
      ref="content"
      :class="['modal-content', { active: isActive }]"
    >
      <div v-if="!isLoading && complementData">
        <!-- Navegador -->
        <div
          v-if="haveBack"
          class="modal-buttons px-4"
        >
          <IconButton
            id="back"
            icon="arrow-left-square-fill"
            size="25"
            :on-click="handleClickBack"
          />
          <div class="line" />
        </div>
        <!-- Conteúdo -->
        <div class="col-12 modal-frame p-4">
          <Typography
            v-if="complementData.title"
            variant="modal-info-title"
          >
            {{ complementData.title }}
          </Typography>

          <div
            v-if="complementData?.complement"
            class="row modal-description"
          >
            <Typography variant="modal-info-description">
              <div
                @click="handleSpanClick"
                v-html="complementData.complement.description"
              />
            </Typography>
          </div>
        </div>
      </div>
      <div
        v-else
        class="m-4"
      >
        <Shimmer class="shimmer header" />
        <Shimmer class="shimmer body" />
        <Shimmer class="shimmer body" />
        <Shimmer class="shimmer body" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .box-content {
    position: fixed;
    height: 100%;
    width: 100%;
    z-index: 1030;
    padding: 0;

    .backdrop {
      background-color: rgba(0, 0, 0, 0);
      backdrop-filter: blur(0px);
      transition:
        background-color 0.36s cubic-bezier(0.32, 0.08, 0.55, 1),
        backdrop-filter 0.36s cubic-bezier(0.32, 0.08, 0.55, 1),
        all 0.3s;
      position: fixed;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;

      &.active {
        background-color: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(10px);
      }
    }

    .modal-content {
      background-color: #222529;
      transition: right 0.3s;
      overflow: auto;
      height: 100%;
      width: 60%;
      z-index: 1;
      position: fixed;
      top: 0;
      right: -60%;
      overflow: auto;

      &.active {
        right: 0;
      }

      @media (max-width: 768px) {
        width: 90%;
        right: -90%;
      }
    }

    .modal-frame {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 12px;
    }

    .modal-description {
      flex: 1;
      padding: 12px;
    }

    .modal-buttons {
      background-color: rgba(34, 37, 41, 0.6);
      z-index: 2;
      position: sticky;
      top: 0;
      flex: 1;
      padding: 12px 12px 0px;
      backdrop-filter: blur(10px);
    }

    .line {
      margin-top: 12px;
      border-bottom: 1px solid #ccc;
    }
  }

  .shimmer {
    &.header {
      height: 55px;
    }

    &.body {
      height: 200px;
    }
  }
</style>
